import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import {
  generateInterpretations,
  generateTransitInterpretation,
  generateSynastryInterpretation,
} from "../services/api";
import api from "../services/api";

const INITIAL_INTERPRETATIONS = {
  planets: [],
  aspects: [],
  synthesis: null,
  transits: [],
  synastry: null,
  chartHash: null,
};

const INITIAL_LOADED_TABS = {
  planets: false,
  dwarf: false,
  majorAspects: false,
  minorAspects: false,
  synthesis: false,
  transits: false,
  synastry: false,
};

const INITIAL_LOADING_TABS = { ...INITIAL_LOADED_TABS };

const MAX_CONTEXT_PLANETS = 12;
const MAX_CONTEXT_ASPECTS = 8;
const REGULAR_PLANETS = ["mercury", "venus", "earth", "mars", "jupiter", "saturn", "uranus", "neptune", "pluto"];
const EXCLUDED_PLANETS = new Set(["sun"]);
const MAJOR_ASPECT_PRIORITY = ["conjunction", "opposition", "square", "trine", "sextile"];

const compactPlanet = (planet = {}) => ({
  name: planet.name,
  label: planet.label,
  sign: planet.sign,
  degreeInSign: planet.degreeInSign ?? planet.degree_in_sign,
  longitude: planet.longitude,
  house: planet.house,
});

const compactAspect = (aspect = {}) => ({
  id: aspect.id,
  entity: aspect.entity,
  planets: aspect.planets,
  aspect: aspect.aspect,
  type: aspect.type,
  orb: aspect.orb,
  applying: aspect.applying,
  separating: aspect.separating,
  strength: aspect.strength,
});

const buildCompactChartPayload = (chartData = {}) => ({
  planets: (chartData.planets || []).slice(0, MAX_CONTEXT_PLANETS).map(compactPlanet),
  aspects: (chartData.aspects || []).slice(0, MAX_CONTEXT_ASPECTS).map(compactAspect),
  birthData: chartData.birthData,
});

const normalizeName = (value) => (value || "").toString().trim().toLowerCase();

const extractAspectTypeCandidates = (status = {}) => {
  const candidates = [];
  const pushCandidate = (value) => {
    const normalized = normalizeName(value);
    if (normalized) candidates.push(normalized);
  };

  pushCandidate(status.selectedType);
  pushCandidate(status.selectedAspectType);
  (status.unlockedTypes || []).forEach(pushCandidate);
  (status.unlocked || []).forEach(pushCandidate);
  return candidates;
};

const selectMajorAspectBucket = (allAspects = [], preferredTypes = []) => {
  const majorAspects = allAspects.filter((aspect) => aspect.type !== "minor");
  if (!majorAspects.length) return [];

  for (const preferredType of preferredTypes) {
    const matches = majorAspects.filter((aspect) => normalizeName(aspect.aspect) === preferredType);
    if (matches.length) return matches;
  }

  for (const fallbackType of MAJOR_ASPECT_PRIORITY) {
    const matches = majorAspects.filter((aspect) => normalizeName(aspect.aspect) === fallbackType);
    if (matches.length) return matches;
  }

  return [];
};

const findPlanetByName = (planets = [], name = "") => {
  const target = normalizeName(name);
  if (!target) return null;
  return planets.find((planet) => {
    const candidates = [planet.name, planet.label, planet.entity].map(normalizeName);
    return candidates.includes(target);
  });
};

const buildScopedPlanetChart = (chartData = {}, planetName) => {
  const allPlanets = chartData.planets || [];
  const targetPlanet = findPlanetByName(allPlanets, planetName) || { name: planetName };
  const relatedAspects = (chartData.aspects || []).filter((aspect) =>
    (aspect.planets || []).some((p) => normalizeName(p) === normalizeName(targetPlanet.name || planetName)),
  );
  return {
    planets: targetPlanet ? [compactPlanet(targetPlanet)] : [],
    aspects: relatedAspects.map(compactAspect),
    birthData: chartData.birthData,
  };
};

const aspectMatches = (candidate = {}, target = {}) => {
  if (candidate.id && target.id && candidate.id === target.id) return true;
  if (candidate.entity && target.entity && candidate.entity === target.entity) return true;
  const candidateKey = (candidate.planets || []).join("-") + candidate.aspect;
  const targetKey = (target.planets || []).join("-") + target.aspect;
  return candidateKey === targetKey;
};

const buildScopedAspectChart = (chartData = {}, targetAspect = {}) => {
  const aspects = chartData.aspects || [];
  const matchedAspect = aspects.find((aspect) => aspectMatches(aspect, targetAspect)) || targetAspect;
  const involvedNames = new Set((matchedAspect.planets || []).map(normalizeName));
  const involvedPlanets = (chartData.planets || []).filter((planet) => {
    const aliases = [planet.name, planet.label, planet.entity].map(normalizeName);
    return aliases.some((alias) => involvedNames.has(alias));
  });
  return {
    planets: involvedPlanets.slice(0, MAX_CONTEXT_PLANETS).map(compactPlanet),
    aspects: [matchedAspect].map(compactAspect),
    birthData: chartData.birthData,
  };
};

const InterpretationsContext = createContext(null);

export const InterpretationsProvider = ({ children }) => {
  const [interpretations, setInterpretations] = useState(INITIAL_INTERPRETATIONS);
  const [interpretationError, setInterpretationError] = useState(null);
  const [loadedTabs, setLoadedTabs] = useState(INITIAL_LOADED_TABS);
  const [loadingTabs, setLoadingTabs] = useState(INITIAL_LOADING_TABS);

  const interpretationsRef = useRef(interpretations);
  const fetchingPlanetsRef = useRef(false);
  const fetchingDwarfRef = useRef(false);

  useEffect(() => {
    interpretationsRef.current = interpretations;
  }, [interpretations]);

  const resetInterpretations = useCallback(() => {
    fetchingPlanetsRef.current = false;
    fetchingDwarfRef.current = false;
    setInterpretations(INITIAL_INTERPRETATIONS);
    setInterpretationError(null);
    setLoadedTabs(INITIAL_LOADED_TABS);
    setLoadingTabs(INITIAL_LOADING_TABS);
  }, []);

  const generatePlanetaryInterpretations = useCallback(async (chartData, category = "regular", cycleIndex) => {
    if (!chartData) return;

    const isDwarf = category === "dwarf";
    const tabKey = isDwarf ? "dwarf" : "planets";
    const fetchingRef = isDwarf ? fetchingDwarfRef : fetchingPlanetsRef;

    if (loadedTabs[tabKey] || fetchingRef.current) return;

    fetchingRef.current = true;
    setLoadingTabs((prev) => ({ ...prev, [tabKey]: true }));
    setInterpretationError(null);

    try {
      const providerToUse = "deepseek";
      const user = JSON.parse(localStorage.getItem("heliocentric_auth"))?.user;
      
      let allowedPlanets = null;
      if (!isDwarf && user?.planTier !== 'premium') {
        try {
          const status = await api.fetchFreePlanetsStatus();
          const unlockedPlanets = (status?.unlocked || [])
            .map(normalizeName)
            .filter((name) => REGULAR_PLANETS.includes(name));

          if (unlockedPlanets.length) {
            allowedPlanets = new Set(unlockedPlanets);
          }
        } catch (e) {
          console.error("Failed to fetch free planets status", e);
        }

        if (!allowedPlanets || !allowedPlanets.size) {
          const safeIndex = Number.isFinite(cycleIndex) ? cycleIndex : 0;
          const selected = REGULAR_PLANETS[safeIndex % REGULAR_PLANETS.length] || REGULAR_PLANETS[0];
          allowedPlanets = new Set([selected]);
        }
      }

      const allPlanets = (chartData.planets || []).filter((p) => {
        const name = (p.name || "").toLowerCase();
        if (EXCLUDED_PLANETS.has(name)) return false;
        const isRegular = REGULAR_PLANETS.includes(name);
        
        if (isDwarf) {
            return !isRegular;
        } else {
            return isRegular && (allowedPlanets ? allowedPlanets.has(name) : true);
        }
      });

      const planetsToProcess = allPlanets;
      if (planetsToProcess.length === 0) {
        setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
        return;
      }

      const MAX_PLANET_RETRIES = 2;

      const fetchPlanetInterpretation = async (planetName) => {
        let attempt = 0;
        let lastError = null;
        while (attempt <= MAX_PLANET_RETRIES) {
          try {
            const scopedChartPayload = buildScopedPlanetChart(chartData, planetName);
            return await generateInterpretations({
              scope: "planet",
              chart: scopedChartPayload,
              planet: planetName,
              providerId: providerToUse,
              birthData: chartData.birthData,
            });
          } catch (error) {
            lastError = error;
            const status = error?.response?.status || error?.statusCode || error?.status;
            const message = error?.response?.data?.message || error.message || "";
            const retryable = status === 429 || (status && status >= 500) || /rate limit|timeout|network/i.test(message || "");
            if (!retryable || attempt >= MAX_PLANET_RETRIES) throw error;
            await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
            attempt += 1;
          }
        }
        throw lastError || new Error("Failed to generate interpretation");
      };

      let successCount = 0;
      let failCount = 0;

      for (const planet of planetsToProcess) {
        const planetName = planet.name;
        try {
          const response = await fetchPlanetInterpretation(planetName);
          if (!response || !response.content) throw new Error(`Empty response for ${planetName}`);

          const planetEntry = {
            id: planetName,
            title: (planet.label || planet.name || "").toUpperCase(),
            content: response.content,
            wordCount: response.wordCount || 0,
            provider: response.provider || providerToUse,
            model: response.model,
            entity: planetName,
          };
          successCount++;
          setInterpretations((prev) => {
            const existingPlanets = prev.planets.filter(
              (p) => !EXCLUDED_PLANETS.has((p.id || p.entity || "").toLowerCase()) && p.id !== planetName,
            );
            return { ...prev, planets: [...existingPlanets, planetEntry], chartHash: chartData.chartHash || prev.chartHash };
          });
        } catch (error) {
          console.error(`Failed to generate interpretation for ${planetName}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
        if (failCount > 0) console.warn(`${failCount} planet(s) failed. ${successCount} loaded successfully.`);
      } else {
        if (planetsToProcess.length > 0) {
          setInterpretationError(`Failed to generate any ${category} planet interpretations`);
        } else {
          setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
        }
      }
    } catch (err) {
      setInterpretationError(err.message || "Failed to generate planetary interpretations");
    } finally {
      fetchingRef.current = false;
      setLoadingTabs((prev) => ({ ...prev, [tabKey]: false }));
    }
  }, [loadedTabs]);

  const generateAspectInterpretations = useCallback(async (chartData, aspectType = "major", limit) => {
    const tabKey = aspectType === "minor" ? "minorAspects" : "majorAspects";
    if (!chartData || loadedTabs[tabKey]) return;

    setLoadingTabs((prev) => ({ ...prev, [tabKey]: true }));
    setInterpretationError(null);

    try {
      const providerToUse = "deepseek";
      const user = JSON.parse(localStorage.getItem("heliocentric_auth"))?.user;
      const allAspects = chartData.aspects || [];
      const targetAspects = allAspects.filter((a) => (aspectType === "major" ? a.type !== "minor" : a.type === "minor"));
      let aspects = limit ? targetAspects.slice(0, limit) : targetAspects;

      if (aspectType === "major" && user?.planTier !== "premium") {
        let preferredTypes = [];
        try {
          const status = await api.fetchFreeAspectsStatus();
          preferredTypes = extractAspectTypeCandidates(status);
        } catch (error) {
          // Optional endpoint: if absent/fails, deterministic frontend fallback is applied.
          preferredTypes = [];
        }

        if (preferredTypes.length) {
          aspects = selectMajorAspectBucket(allAspects, preferredTypes);
        } else {
          let unlockedPlanets = [];
          try {
            const planetStatus = await api.fetchFreePlanetsStatus();
            unlockedPlanets = (planetStatus?.unlocked || [])
              .map(normalizeName)
              .filter((name) => REGULAR_PLANETS.includes(name));
          } catch (error) {
            unlockedPlanets = [];
          }

          if (unlockedPlanets.length >= REGULAR_PLANETS.length) {
            aspects = selectMajorAspectBucket(allAspects);
          } else {
            aspects = [];
          }
        }
      }

      let successCount = 0;

      for (const aspect of aspects) {
        const aspectId = aspect.planets?.join("-") || aspect.id;
        const exists = interpretationsRef.current?.aspects?.some((a) => a.id === aspectId);
        if (exists) {
          successCount++;
          continue;
        }

        try {
          const response = await generateInterpretations({
            scope: "aspect",
            chart: buildScopedAspectChart(chartData, aspect),
            aspect: aspect,
            providerId: providerToUse,
            tier: user?.planTier,
            birthData: chartData.birthData,
          });

          const formatPlanetName = (name = "") =>
            name.toString().replace(/[_-]+/g, " ").split(" ").filter(Boolean)
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ");
          const formattedPlanets = (aspect.planets || []).map((p) => formatPlanetName(p));
          const formattedType = aspect.type && typeof aspect.type === "string"
            ? aspect.type.charAt(0).toUpperCase() + aspect.type.slice(1).toLowerCase() : aspect.type;

          const aspectEntry = {
            id: aspectId,
            title: `${formattedPlanets.join(" ")} ${aspect.aspect} • ${formattedType} Aspect`,
            content: response.content,
            wordCount: response.wordCount,
            provider: response.provider,
            model: response.model,
            type: aspect.type,
            strength: aspect.strength,
          };
          successCount++;
          setInterpretations((prev) => ({
            ...prev,
            aspects: [...prev.aspects, aspectEntry],
            chartHash: chartData.chartHash || prev.chartHash,
          }));
        } catch (err) {
          console.error(`Failed to generate interpretation for aspect ${aspect.id}:`, err);
        }
      }

      if (successCount > 0) setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
      else if (aspects.length > 0) setInterpretationError(`Failed to generate any ${aspectType} aspect interpretations`);
      else setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
    } catch (err) {
      setInterpretationError(err.message || "Failed to generate aspect interpretations");
    } finally {
      setLoadingTabs((prev) => ({ ...prev, [tabKey]: false }));
    }
  }, [loadedTabs]);

  const generateSynthesisInterpretation = useCallback(async (chartData) => {
    if (!chartData || loadedTabs.synthesis) return;
    setLoadingTabs((prev) => ({ ...prev, synthesis: true }));
    setInterpretationError(null);

    try {
      const response = await generateInterpretations({
        scope: "synthesis",
        chart: buildCompactChartPayload(chartData),
        providerId: "deepseek",
        birthData: chartData.birthData,
      });
      if (!response) throw new Error("Empty synthesis response");

      setInterpretations((prev) => ({
        ...prev,
        synthesis: {
          id: "synthesis",
          title: "Soul Mission & Integrative Themes",
          content: response.content,
          wordCount: response.wordCount,
          provider: response.provider,
          model: response.model,
        },
        chartHash: response.chartHash || prev.chartHash,
      }));
      setLoadedTabs((prev) => ({ ...prev, synthesis: true }));
    } catch (err) {
      setInterpretationError(err.message || "Failed to generate synthesis interpretation");
    } finally {
      setLoadingTabs((prev) => ({ ...prev, synthesis: false }));
    }
  }, [loadedTabs]);

  const generateTransitForChart = useCallback(async (chartData, transitPayload) => {
    if (!chartData || !transitPayload) return;
    setLoadingTabs((prev) => ({ ...prev, transits: true }));
    setInterpretationError(null);

    try {
      const response = await generateTransitInterpretation({
        chart: buildCompactChartPayload(chartData),
        transit: transitPayload,
        providerId: "deepseek",
        tier: "premium",
      });
      if (!response) throw new Error("Empty transit response");

      const entry = { ...response, id: transitPayload.id || `transit-${Date.now()}`, title: transitPayload.description || "Transit Insight" };
      setInterpretations((prev) => ({ ...prev, transits: [entry], chartHash: response.chartHash || prev.chartHash }));
      setLoadedTabs((prev) => ({ ...prev, transits: true }));
    } catch (err) {
      setInterpretationError(err.message || "Failed to generate transit interpretation");
    } finally {
      setLoadingTabs((prev) => ({ ...prev, transits: false }));
    }
  }, []);

  const generateSynastryForChart = useCallback(async (synastryPayload) => {
    if (!synastryPayload) return;
    setLoadingTabs((prev) => ({ ...prev, synastry: true }));
    setInterpretationError(null);

    try {
      const response = await generateSynastryInterpretation({
        synastry: synastryPayload,
        providerId: "deepseek",
        tier: "premium",
      });
      if (!response) throw new Error("Empty synastry response");

      const entry = { ...response, id: synastryPayload.id || "synastry", title: synastryPayload.title || "Synastry Insights" };
      setInterpretations((prev) => ({ ...prev, synastry: entry, chartHash: response.chartHash || prev.chartHash }));
      setLoadedTabs((prev) => ({ ...prev, synastry: true }));
    } catch (err) {
      setInterpretationError(err.message || "Failed to generate synastry interpretation");
    } finally {
      setLoadingTabs((prev) => ({ ...prev, synastry: false }));
    }
  }, []);

  const value = {
    interpretations,
    interpretationError,
    loadingTabs,
    loadedTabs,
    generatePlanetaryInterpretations,
    generateAspectInterpretations,
    generateSynthesisInterpretation,
    generateTransitForChart,
    generateSynastryForChart,
    setInterpretations,
    setInterpretationError,
    resetInterpretations,
  };

  return (
    <InterpretationsContext.Provider value={value}>
      {children}
    </InterpretationsContext.Provider>
  );
};

export const useInterpretations = () => {
  const context = useContext(InterpretationsContext);
  if (!context) {
    throw new Error("useInterpretations must be used within InterpretationsProvider");
  }
  return context;
};
